import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpenIcon, DocumentTextIcon, MagnifyingGlassIcon, ScaleIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Reveal, { revealProps } from "../../common/Reveal";
import { relativeTime } from "../../common/relativeTime";
import { TagChip } from "../../Agency/AgencyDocuments/tagColors";
import { POLICY_DOC_TYPE_LABELS, PolicyDocumentItem } from "../api";

const TYPE_ICON: Record<PolicyDocumentItem["docType"], React.FC<{ className?: string }>> = { policy: BookOpenIcon, statute: ScaleIcon, other: DocumentTextIcon };

interface Props {
  documents: PolicyDocumentItem[];
  loading: boolean;
}

// Every document the department uploaded, in the same table the
// Investigations Files tab uses.
const PolicyDocumentsTab: React.FC<Props> = ({ documents, loading }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return documents.filter((d) => !s || d.title.toLowerCase().includes(s) || d.tags.some((t) => t.name.toLowerCase().includes(s)));
  }, [documents, q]);

  if (!loading && documents.length === 0) {
    return (
      <Reveal className="rounded-xl border border-dashed border-base-300 bg-base-200/40 px-6 py-12 text-center">
        <BookOpenIcon className="size-10 mx-auto text-base-content/40" />
        <h2 className="mt-3 text-lg font-semibold text-base-content">Your department hasn't uploaded any policies yet</h2>
        <p className="mt-1 text-sm text-base-content/60 max-w-md mx-auto">
          When an agency admin uploads the policy manual or local ordinances, they appear here, and your reports start citing them.
        </p>
      </Reveal>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <label className="relative block w-full max-w-sm">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-base-content/40" />
          <input data-tour="policies-search" className="input input-bordered input-sm w-full pl-9 bg-base-100 focus:outline-none focus:border-blue-500" placeholder="Search policies and topics" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <span className="text-xs text-base-content/50">
          {documents.length} document{documents.length === 1 ? "" : "s"}
        </span>
      </div>
      <Reveal className="overflow-x-auto rounded-xl border border-base-300">
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Document</th>
              <th>Type</th>
              <th>Topics</th>
              <th className="text-right">Pages</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d, i) => {
              const Icon = TYPE_ICON[d.docType] || DocumentTextIcon;
              const enter = revealProps(i);
              return (
                <tr key={d.id} data-tour="policy-doc-row" style={enter.style} className={cn("hover cursor-pointer", enter.className)} onClick={() => navigate(`/policies/documents/${d.id}`)}>
                  <td className="max-w-md">
                    <span className="flex items-center gap-2 text-left">
                      <Icon className="size-4 text-base-content/50 shrink-0" />
                      <span className="truncate font-medium text-base-content hover:underline">{d.title}</span>
                    </span>
                  </td>
                  <td className="text-xs text-base-content/70">{POLICY_DOC_TYPE_LABELS[d.docType]}</td>
                  <td>
                    <span className="flex flex-wrap gap-1">
                      {d.tags.slice(0, 5).map((t) => (
                        <TagChip key={t.id} name={t.name} color={t.color} />
                      ))}
                      {d.tags.length > 5 && <span className="text-[11px] text-base-content/50 self-center">+{d.tags.length - 5}</span>}
                    </span>
                  </td>
                  <td className="text-xs text-base-content/70 text-right tabular-nums">{d.pageCount ?? ""}</td>
                  <td className="text-xs text-base-content/60 whitespace-nowrap">{relativeTime(d.updatedAt || d.createdAt)}</td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-sm text-base-content/60 py-6">
                  No documents match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Reveal>
    </div>
  );
};

export default PolicyDocumentsTab;
